import { observable, action, makeObservable } from 'mobx';
import { BaseViewStore } from 'core/stores';
import { BaasicTableSettingsForm } from 'common/forms';
import { ErrorModalParams, ConfirmModalParams } from 'core/models';
import { isSome } from 'core/utils';
import _ from 'lodash';
import { defaultTemplateArray, fulfillmentOrdersGroupByOptions } from 'common/constants';
import { BaasicTableSettings } from 'core/components';
import BaasicDropdownViewStore from './BaasicDropdownViewStore';

class BaasicTableSettingsViewStore extends BaseViewStore {
	//Not observables

	@observable templates;
	@observable selectedColumns;
	@observable availableColumns;
	@observable currentSelectedColumns;
	@observable allColumns;
	@observable moveUpButton;
	@observable moveDownButton;
	@observable addButton;
	@observable removeButton;
	@observable newTemplateConfirmed;
	@observable isInput;
	@observable templateName;
	@observable newTemplate;
	@observable templateAlreadyExists;
	@observable templateAlreadyExistsColumns;
	@observable applyButton;
	@observable saveButton;
	@observable saveAsNewButton;
	@observable isTemplateNameEmpty;
	find;
	change;
	baasicTableSettingsKey;
	allColumns = [];
	lockedColumns = [];
	availableColumnsInital = [];
	selectedColumnsIntial = [];
	allColumnsInitial = [];
	defaultColumns = [];

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
	}

	form = new BaasicTableSettingsForm({
		onSuccess: async () => {
			this.saveAndApplyTableSettings();
		},
	});

	@action.bound
	initialize = async (allColumns, baasicTableSettingsKey, change, find, templates) => {
		this.setEmptyInitialValues();
		this.find = find;
		this.change = change;
		this.baasicTableSettingsKey = baasicTableSettingsKey;

		// Set initial column array values.
		this.allColumnsInitial = allColumns;

		_.each(allColumns, column => {
			if (column.enabled == null || column.enabled == true) {
				if (column.selected == true) {
					if (column.locked == true) {
						this.lockedColumns.push(column.key);
					} else {
						this.selectedColumnsIntial.push(column);
					}
				} else {
					this.availableColumnsInital.push(column);
				}
			}
		});
		this.templates = templates;
		this.getDefaultTemplate();
	};

	@action.bound
	setTemplates = async (tableData, baasicTableSettingsKey, setAsChanged, find) => {
		let defaultTemplateColumns = null;
		let templates = await this.rootStore.userSettingStore.getSetting(baasicTableSettingsKey);
		if (!templates && baasicTableSettingsKey === 'FulfillmentOrdersTableSettings') {
			let defaultTemplate = [['default:default template', defaultTemplateArray]];
			await this.rootStore.userSettingStore.setSetting(baasicTableSettingsKey, defaultTemplate);
			templates = await this.rootStore.userSettingStore.getSetting(baasicTableSettingsKey);
		}
		let lockedColumns = [];
		let selectedColumns = [];

		// Set locked columns.
		_.each(tableData.columns, item => {
			if (item.locked) {
				lockedColumns.push(item.key);
			}
		});

		if (templates) {
			_.each(templates, template => {
				let templateName = template[0];
				let diff = _.difference(lockedColumns, template[1]);
				if (diff && diff.length != 0) {
					_.forEach(diff, item => {
						template[1].unshift(item);
					});
				}
				if (templateName.includes('default')) {
					defaultTemplateColumns = template[1];
				}
			});
		}

		if (defaultTemplateColumns) {
			_.forEach(tableData.columns, column => {
				if (defaultTemplateColumns.includes(column.key)) {
					column.selected = true;
					selectedColumns.push(column);
				}
			});
		} else {
			_.forEach(tableData.columns, column => {
				if (column.selected) {
					selectedColumns.push(column);
				}
			});
		}

		// Sort selected columns.
		if (defaultTemplateColumns) {
			tableData.columns.sort((a, b) => defaultTemplateColumns.indexOf(a.key) - defaultTemplateColumns.indexOf(b.key));
		}

		_.forEach(tableData.columns, column => {
			if (!selectedColumns.includes(column)) {
				column.selected = false;
			}
		});

		await this.initialize(tableData.columns, baasicTableSettingsKey, setAsChanged, find, templates);
	};

	// On modal open.
	@action.bound
	openTableSettingsModal = async () => {
		this.form.clear();
		// Set columns arrays.
		this.selectedColumns = this.selectedColumnsIntial;
		this.availableColumns = this.availableColumnsInital;
		this.allColumns = this.allColumnsInitial;
		this.currentSelectedColumns = [];
		this.isChanged = false;
		this.newTemplateConfirmed = false;
		this.isInput = false;
		this.applyButton = false;
		this.saveAsNewButton = true;
		this.templateAlreadyExists = false;
		this.templateAlreadyExistsColumns = false;
		this.newTemplate = true;
		// Buttons state set.
		this.setSaveButton();
		this.arrowButtonCheck();
		this.setTemplateColumnOrder();
		this.openModal({
			onClose: this.closeTableSettingsModal,
			data: this,
			component: BaasicTableSettings,
			className: 'ReactModal--lrg ReactModal--responsive',
		});
	};

	// Disables or enables save button depending if there are any existing templates.
	@action.bound
	setSaveButton = () => {
		if (this.templates && this.templates.length != 0) {
			this.saveButton = true;
		} else {
			this.saveButton = false;
		}
	};

	// Sets template column order so that validation can function properly
	@action.bound
	setTemplateColumnOrder = () => {
		if (this.templateDropdownStore.value) {
			let sortedSelectedColumns = [];
			let defaultTemplateColumns = [];
			defaultTemplateColumns = _.find(this.templates, item => {
				if (item[0] === this.templateDropdownStore.value.value) {
					return item[0];
				}
			});
			defaultTemplateColumns = defaultTemplateColumns[1];
			sortedSelectedColumns = _.sortBy(this.selectedColumns, function(item) {
				return defaultTemplateColumns.indexOf(item.key);
			});
			this.selectedColumns = _.cloneDeep(sortedSelectedColumns);
		}
	};

	// On column select action.
	@action.bound
	onColumnSelect = value => {
		let selectedColumn = this.allColumns.find(column => column.key === value);
		// If already selected remove it else mark it selected.
		if (this.currentSelectedColumns.length > 0 && this.currentSelectedColumns.some(column => column.key === value)) {
			this.currentSelectedColumns = _.reject(this.currentSelectedColumns, { key: value });
		} else {
			if (this.currentSelectedColumns.length > 0) {
				if (
					this.availableColumns.some(column => column.key === value) &&
					this.currentSelectedColumns.some(column => column.selected != false)
				) {
					for (let i = 0; i < this.selectedColumns.length; i++) {
						if (this.currentSelectedColumns.some(column => column.key === this.selectedColumns[i].key)) {
							this.removeActive(this.selectedColumns[i].key);
						}
					}
					this.currentSelectedColumns = [];
				} else if (
					this.selectedColumns.some(column => column.key === value) &&
					this.currentSelectedColumns.some(column => column.selected != true)
				) {
					for (let i = 0; i < this.availableColumns.length; i++) {
						if (this.currentSelectedColumns.some(column => column.key === this.availableColumns[i].key)) {
							this.removeActive(this.availableColumns[i].key);
						}
					}
					this.currentSelectedColumns = [];
				}
			}
			this.currentSelectedColumns.push(selectedColumn);
		}
		this.selectRow(selectedColumn.key);
		this.arrowButtonCheck();
	};

	// On add/remove selected columns action.
	@action.bound
	onTransferColumn = action => {
		this.saveAsNewButton = true;
		let selectedFields = [];
		if (action === 'add') {
			selectedFields = _.intersectionWith(
				this.currentSelectedColumns,
				this.availableColumns,
				({ key: a }, { key: b }) => a === b
			);
			if (isSome(selectedFields)) {
				_.each(selectedFields, column => {
					column.selected = true;
					this.selectedColumns.push(column);
					this.currentSelectedColumns = _.reject(this.currentSelectedColumns, col => col.key === column.key);
					this.availableColumns = _.reject(this.availableColumns, col => col.key === column.key);
				});
			}
		} else if (action === 'remove') {
			selectedFields = _.intersectionWith(
				this.currentSelectedColumns,
				this.selectedColumns,
				({ key: a }, { key: b }) => a === b
			);
			if (isSome(selectedFields)) {
				_.each(selectedFields, column => {
					column.selected = false;
					this.availableColumns.push(column);
					this.currentSelectedColumns = _.reject(this.currentSelectedColumns, col => col.key === column.key);
					this.selectedColumns = _.reject(this.selectedColumns, col => col.key === column.key);
				});
			}
		}
		if (this.templates && this.templates.length != 0) {
			this.validateTemplate();
		}
		this.arrowButtonCheck();
	};

	// On move up/down column action.
	@action.bound
	moveColumn = direction => {
		this.saveAsNewButton = true;
		if (
			this.currentSelectedColumns.length > 0 &&
			_.intersectionWith(this.currentSelectedColumns, this.selectedColumns, ({ key: a }, { key: b }) => a === b)
				.length == 1
		) {
			let selectedColumnIndex = _.findIndex(this.selectedColumns, ['key', _.head(this.currentSelectedColumns).key]);
			if (direction === 'up' && selectedColumnIndex > 0) {
				this.swap(selectedColumnIndex, selectedColumnIndex - 1);
			}
			if (direction === 'down' && selectedColumnIndex < this.selectedColumns.length - 1) {
				this.swap(selectedColumnIndex, selectedColumnIndex + 1);
			}
		}
		if (this.templates && this.templates.length != 0) {
			this.validateTemplate();
		}
	};

	@action.bound
	swap = (indexA, indexB) => {
		let temp = this.selectedColumns[indexA];
		this.selectedColumns[indexA] = this.selectedColumns[indexB];
		this.selectedColumns[indexB] = temp;
		this.arrowButtonCheck();
	};

	@action.bound
	removeActive = value => {
		let divTableRow = document.getElementById('table__settings__item' + value);
		if (divTableRow.className.includes('is-active')) {
			divTableRow.className = divTableRow.className.split(' ').pop();
		}
	};

	@action.bound
	selectRow = value => {
		let rowId = 'table__settings__item' + value;
		let divTableRow = document.getElementById(rowId);
		if (divTableRow.className.includes('is-active')) {
			divTableRow.className = divTableRow.className.split(' ').pop();
		} else {
			divTableRow.className = 'is-active ' + divTableRow.className;
		}
	};

	// Buttons disable check.
	@action.bound
	arrowButtonCheck = () => {
		if (this.currentSelectedColumns.length >= 1) {
			if (
				_.intersectionWith(this.currentSelectedColumns, this.selectedColumns, ({ key: a }, { key: b }) => a === b)
					.length == 1
			) {
				let selectedColumnIndex = _.findIndex(this.selectedColumns, ['key', _.head(this.currentSelectedColumns).key]);
				if (selectedColumnIndex == 0) {
					this.moveUpButton = true;
					if (selectedColumnIndex == this.selectedColumns.length - 1) {
						this.moveDownButton = true;
					} else {
						this.moveDownButton = false;
					}
				} else if (selectedColumnIndex == this.selectedColumns.length - 1) {
					this.moveUpButton = false;
					this.moveDownButton = true;
				} else {
					this.moveUpButton = false;
					this.moveDownButton = false;
				}
			} else {
				this.moveUpButton = true;
				this.moveDownButton = true;
			}
			if (
				_.intersectionWith(this.currentSelectedColumns, this.selectedColumns, ({ key: a }, { key: b }) => a === b)
					.length >= 1
			) {
				this.removeButton = false;
				this.addButton = true;
			} else if (
				_.intersectionWith(this.currentSelectedColumns, this.availableColumns, ({ key: a }, { key: b }) => a === b)
					.length >= 1
			) {
				this.removeButton = true;
				this.addButton = false;
			}
		} else {
			this.moveUpButton = true;
			this.moveDownButton = true;
			this.removeButton = true;
			this.addButton = true;
		}
	};

	// Templates related actions.
	@action.bound
	getDefaultTemplate = () => {
		this.templateDropdownStore = new BaasicDropdownViewStore({
			staticOptions: [],
			onChangeCallback: value => this.onTemplateSelect(value),
		});
		const options = _.map(this.templates, item => {
			if (item[0].includes('default')) {
				let optionItem = { label: item[0].split(':').pop(), value: item[0] };
				this.templateDropdownStore.value = optionItem;
				return optionItem;
			} else {
				return { label: item[0], value: item[0] };
			}
		});
		this.templateDropdownStore.options = options;
	};

	@action.bound
	onTemplateSelect = item => {
		if (item) {
			this.templateAlreadyExists = false;
			this.saveAsNewButton = false;
			this.newTemplateConfirmed = false;
			let tempIndex = _.findIndex(this.templates, template => template[0] === item.value);
			let tempSelected = this.templates[tempIndex];
			this.allColumns = this.allColumnsInitial;
			this.setEmptyValues();
			if (tempIndex > -1) {
				_.each(this.allColumns, column => {
					if (!column.locked) {
						if (_.findIndex(tempSelected[1], item => item === column.key) > -1) {
							column.selected = true;
							this.selectedColumns.push(column);
						} else {
							column.selected = false;
							this.availableColumns.push(column);
						}
					}
				});
			}
			this.cancelNewTemplate();
			this.setTemplateColumnOrder();
			this.validateTemplate();
			this.setSaveButton();
		}
	};

	@action.bound
	onTemplateDelete = () => {
		let selected = this.templateDropdownStore.value && this.templateDropdownStore.value.value;
		if (selected) {
			let idxToRemove = _.findIndex(this.templates, item => item[0] == this.templateDropdownStore.value.value);
			if (idxToRemove > -1) {
				this.templates.splice(idxToRemove, 1);
				if (this.templates.length) {
					if (idxToRemove >= this.templates.length) idxToRemove = this.templates.length - 1;
					let item = this.templates[idxToRemove];
					if (item[0].includes('default')) {
						let optionItem = { label: item[0].split(':').pop(), value: item[0] };
						this.templateDropdownStore.setValue(optionItem);
					} else {
						this.templateDropdownStore.setValue({ label: item[0], value: item[0] });
					}
				} else {
					this.templateDropdownStore.setValue(null);
				}
				this.onTemplateApply(false);
				this.templateDropdownStore.options = _.map(this.templates, item => {
					if (item[0].includes('default')) {
						let optionItem = { label: item[0].split(':').pop(), value: item[0] };
						this.templateDropdownStore.value = optionItem;
						return optionItem;
					} else {
						return { label: item[0], value: item[0] };
					}
				});
			}
			this.setSaveButton();
		}
	};

	@action.bound
	onTemplateApply = async (close = false) => {
		this.suspend();
		let templatesTmp = [];
		_.each(this.templates, item => {
			let templateName = item[0];
			let selectedName = this.templateDropdownStore.value.value;
			if (templateName.includes('default')) {
				templateName = templateName.split(':').pop();
			}
			if (selectedName.includes('default')) {
				selectedName = selectedName.split(':').pop();
			}
			if (templateName === selectedName) {
				templateName = 'default:' + item[0];
			}
			item[0] = templateName;
			templatesTmp.push(item);
		});
		await this.rootStore.userSettingStore.setSetting(this.baasicTableSettingsKey, templatesTmp);
		this.change();
		this.isChanged = true;
		if (close) this.closeTableSettingsModal();
		this.resume();
	};

	@action.bound
	onSaveAndApply = () => {
		let { templateName } = this.form.values();
		if (templateName === '' && this.templateDropdownStore.value) {
			this.saveAndApplyTableSettings();
		}
	};

	@action.bound
	saveAndApplyTableSettings = async () => {
		this.suspend();
		let templates = [];
		if (this.templates) {
			_.each(this.templates, item => {
				if (item[0].includes('default')) {
					item[0] = item[0].split(':').pop();
				}
				templates.push(item);
			});
		}
		let { templateName } = this.form.values();
		let isEmptyName = false;
		if (templateName === '') {
			isEmptyName = true;
			this.isTemplateNameEmpty = true;
			templateName = this.templateDropdownStore.value.label;
			templates = _.filter(templates, item => {
				return item[0] != this.templateDropdownStore.value.label;
			});
		} else {
			this.isTemplateNameEmpty = false;
		}
		const defaultTemplateName = 'default:' + templateName;

		let columnKeys = this.lockedColumns.slice();
		_.each(this.selectedColumns, column => {
			columnKeys.push(column.key);
		});
		let template = [];
		let existingTemplate;
		if (this.newTemplateConfirmed) {
			existingTemplate = _.find(this.templates, item => {
				return item[0] == defaultTemplateName || item[0] == defaultTemplateName.split(':').pop();
			});
		}

		if (!existingTemplate) {
			if (defaultTemplateName === 'default:default template') {
				columnKeys = defaultTemplateArray;
			}
			template.push(defaultTemplateName);
			template.push(columnKeys);
			templates.push(template);
			await this.rootStore.userSettingStore.setSetting(this.baasicTableSettingsKey, templates);
			if (defaultTemplateName === 'default:default template') {
				this.rootStore.notificationStore.success('BAASICTABLESETTINGS.DEFAULTTEMPLATE');
			} else {
				this.rootStore.notificationStore.success(['BAASICTABLESETTINGS.SUCCESS', { name: templateName }]);
			}
			this.change();
			this.isChanged = true;
			this.closeTableSettingsModal();
			this.resume();
		} else {
			if (!isEmptyName) {
			}
		}
	};

	// Checks if the new template is opened and does validation in case it is.
	@action.bound
	validateTemplate = () => {
		if (this.newTemplateConfirmed && this.templates) {
			this.templateColumnsCheck();
			this.validateTemplateName();
		} else {
			this.saveButton = true;
		}
	};

	//Basic actions
	@action.bound
	setEmptyValues = () => {
		if (this.currentSelectedColumns.length > 0) {
			_.each(this.currentSelectedColumns, item => this.removeActive(item.key));
		}
		this.currentSelectedColumns = [];
		this.selectedColumns = [];
		this.availableColumns = [];
		this.arrowButtonCheck();
	};

	@action.bound
	setEmptyInitialValues = () => {
		this.availableColumnsInital = [];
		this.selectedColumnsIntial = [];
		this.allColumnsInitial = [];
		this.lockedColumns = [];
	};

	@action.bound
	validateTemplateName = () => {
		let { templateName } = this.form.values();
		if (!this.templateAlreadyExistsColumns) {
			if (
				isSome(this.templates) &&
				(this.templates.some(template => template[0] === templateName.trim()) ||
					this.templates.some(template => template[0] === 'default:' + templateName.trim()))
			) {
				this.saveButton = false;
				this.templateAlreadyExists = true;
			} else {
				this.templateAlreadyExists = false;
				this.isTemplateNameEmpty = false;
			}
		} else {
			this.saveButton = false;
		}
	};

	@action.bound
	templateColumnsCheck = () => {
		this.templateAlreadyExistsColumns = false;
		let tempTemplate = this.lockedColumns.slice();
		if (isSome(this.templates)) {
			_.each(this.selectedColumns, column => {
				tempTemplate.push(column.key);
			});
			this.templates.forEach(templa => {
				if (_.isEqual(tempTemplate.slice(), templa[1].slice())) {
					if (templa[0].includes('default')) {
						this.templateAlreadyExistsColumns = templa[0].split(':').pop();
					} else {
						this.templateAlreadyExistsColumns = templa[0];
					}
				}
			});
			if (this.templateAlreadyExistsColumns && this.newTemplateConfirmed) {
				this.saveButton = false;
			} else {
				this.saveButton = true;
			}
		}
	};

	@action.bound
	openConfirmModal = () => {
		this.confirmModal.open({
			message: ['BAASICTABLESETTINGS.TEMPLATEDELETE', { name: this.templateDropdownStore.value.label }],
		});
	};

	@action.bound
	onBlur = () => {
		let { templateName } = this.form.values();
		if (templateName === '') {
			this.isInput = false;
		}
	};

	@action.bound
	closeTableSettingsModal = () => {
		if (this.isChanged) this.find();
		this.getDefaultTemplate();
		this.rootStore.viewStore.selectedModal.close();
	};

	@action.bound
	onNewTemplate = () => {
		this.newTemplateConfirmed = true;
		this.isInput = true;
		this.applyButton = false;
		this.newTemplate = false;
		this.validateTemplate();
		this.saveButton = true;
	};

	@action.bound
	cancelNewTemplate = () => {
		this.form.clear();
		this.newTemplateConfirmed = false;
		this.templateAlreadyExists = false;
		this.isInput = false;
		this.newTemplate = true;
		this.templateAlreadyExistsColumns = false;
		this.setSaveButton();
	};

	errorModal = new ErrorModalParams({
		onClose: this.closeErrorModal,
	});

	confirmModal = new ConfirmModalParams({
		onConfirm: this.onTemplateDelete,
	});
}

export default BaasicTableSettingsViewStore;



// WEBPACK FOOTER //
// ./src/common/stores/BaasicTableSettingsViewStore.js